import { Matrix4 } from 'three';
import { Store } from 'Application/Store/Store';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { toogleAnimationVisible, setKeyframeToBegin, setKeyframeToEnd } from 'Domain/Scene/Manager/SceneManager.js';
import { getTimes, showComparativeElement } from 'Domain/Scene/Handler/AnimationHandler';
import { SET_SELECTED_COMPARATIVE_VERSION, SET_SELECTED_VERSION } from 'Application/Store/Actions/ViewportTypes';
import {
    setAnimationToEnd
} from './ToothSelectionHandler';

/* ──────────────────────────────────────────────
   APPLY MODIFICATION
   ────────────────────────────────────────────── */

export const applyModification = async (id, applyOnComparative, context) => {
    const numericId = parseInt(id, 10);
    if (isOriginalVersion(numericId)) {
        await resetToOriginalVersion(applyOnComparative, context);
        return;
    }
    if (!hasModifications()) return;

    prepareModificationApplication();
    dispatchVersionUpdate(id, applyOnComparative);
    await scheduleModificationApplication(id, applyOnComparative, context);
};

const isOriginalVersion = (id) => id === -1;

const resetToOriginalVersion = async (applyOnComparative, context) => {
    Store.dispatch(toogleAnimationVisible('none'));
    resetAllTeethMovements(context.globalTeethObjects);

    await new Promise(resolve => {
        setTimeout(() => {
            dispatchOriginalVersionUpdate(applyOnComparative, context);
            restoreAllTeethToOriginalState(applyOnComparative, context);
            forceMixerToCurrentKeyframe();
            resolve();
        }, 300);
    });
};

const forceMixerToCurrentKeyframe = () => {
    const action = threeEngine.getAction();
    const mixer = threeEngine.getMixer();
    if (!action || !mixer) return;
    const times = getTimes();
    const keyframe = Store.getState().AnimationReducer.keyframe;
    action.time = times[keyframe];
    mixer.update(0);
};

const restoreAllTeethToOriginalState = (applyOnComparative, context) => {
    const teethToRestore = getTeethForReset(applyOnComparative, context);
    teethToRestore.forEach(toothData => toothData.restoreToOriginalState());
};

const getTeethForReset = (applyOnComparative, context) => {
    if (applyOnComparative) {
        return [
            ...Object.values(threeEngine.getMaxiExtraDataComparative()),
            ...Object.values(threeEngine.getMandiExtraDataComparative())
        ];
    }
    return [
        ...Object.values(context.maxiMatrixHandler),
        ...Object.values(context.mandiExtraHandler)
    ];
};

const dispatchOriginalVersionUpdate = (applyOnComparative, context) => {
    if (applyOnComparative) {
        Store.dispatch({ type: SET_SELECTED_COMPARATIVE_VERSION, payload: -1 });
        showComparativeElement(true);
    } else {
        Store.dispatch({ type: SET_SELECTED_VERSION, payload: -1 });
        const isEditMode = Store.getState().ViewportReducer.mode === 'edition';
        if (isEditMode) {
            Store.dispatch(setKeyframeToEnd(getTimes()));
            setAnimationToEnd(true);
        } else {
            Store.dispatch(setKeyframeToBegin());
            Store.dispatch(toogleAnimationVisible('flex'));
            setAnimationToEnd(false);
        }
    }
};

const hasModifications = () => !!meshSelectionStore.getModifications();

const prepareModificationApplication = () => {
    Store.dispatch(toogleAnimationVisible('none'));
};

const resetAllTeethMovements = (globalTeethObjects) => {
    globalTeethObjects.forEach(toothData => {
        toothData.mouvements = [];
    });
};

const scheduleModificationApplication = async (id, applyOnComparative, context) => {
    const SCENE_UPDATE_DELAY = 300;
    await new Promise(resolve => {
        setTimeout(() => {
            initializePivotsForHandlers(applyOnComparative, context);
            applyToothModifications(id, applyOnComparative, context);
            resolve();
        }, SCENE_UPDATE_DELAY);
    });
};

const initializePivotsForHandlers = (applyOnComparative, context) => {
    if (applyOnComparative) {
        initializePivotsForHandler(threeEngine.getMaxiExtraDataComparative());
        initializePivotsForHandler(threeEngine.getMandiExtraDataComparative());
    } else {
        initializePivotsForHandler(context.maxiMatrixHandler);
        initializePivotsForHandler(context.mandiExtraHandler);
    }
};

const initializePivotsForHandler = (handler) => {
    Object.values(handler).forEach(initializeToothPivot);
};

const initializeToothPivot = (toothData) => {
    if (toothData.pivotAlreadyPlaced) {
        toothData.pivot.updateMatrix();
    } else {
        setupNewPivot(toothData);
    }
};

const setupNewPivot = (toothData) => {
    toothData.updateWorldPosition();
    toothData.pivot.add(toothData.refObj);
    toothData.placePivot();
    toothData.offsetRefObj();
    toothData.pivotAlreadyPlaced = true;
};

const applyToothModifications = (id, applyOnComparative, context) => {
    const currentModifications = meshSelectionStore.getModifications();
    const selectedModif = currentModifications[id].modifications;
    applyJawModifications(selectedModif, 'mandi', 31, 47, { 37: 40 }, applyOnComparative, context);
    applyJawModifications(selectedModif, 'maxi', 11, 27, { 17: 20 }, applyOnComparative, context);
};

const applyJawModifications = (selectedModif, jawType, startID, endID, skipIndices, applyOnComparative, context) => {
    for (let i = startID; i <= endID; i++) {
        if (!selectedModif[jawType][i]) continue;
        const toothData = getToothData(i, applyOnComparative, context);
        if (toothData) {
            applyMatrixToTooth(toothData, selectedModif[jawType][i]);
        }
        if (skipIndices[i]) i = skipIndices[i];
    }
};

const getToothData = (toothId, applyOnComparative, context) => {
    if (applyOnComparative) {
        return threeEngine.getMaxiExtraDataComparative()[toothId] || threeEngine.getMandiExtraDataComparative()[toothId];
    }
    return context.maxiMatrixHandler[toothId] || context.mandiExtraHandler[toothId];
};

const applyMatrixToTooth = (toothData, modificationData) => {
    reparentToothToPivot(toothData);
    applyTransformMatrix(toothData, modificationData);
    toothData.pivotAlreadyPlaced = true;
};

const reparentToothToPivot = (toothData) => {
    if (isMandiTooth(toothData)) {
        const mandiGroupGlobal = threeEngine.getMandiGroup();
        if (mandiGroupGlobal) mandiGroupGlobal.remove(toothData.refObj);
    } else {
        threeEngine.getScene().remove(toothData.refObj);
    }
    toothData.pivot.add(toothData.refObj);
};

const isMandiTooth = (toothData) => toothData.jawSide === 1 && threeEngine.getMandiGroup();

const applyTransformMatrix = (toothData, modificationData) => {
    const newMatrix = createMatrixFromElements(modificationData.elements);
    toothData.pivot.matrix.copy(newMatrix);
    decomposePivotMatrix(toothData);
};

const createMatrixFromElements = (elements) => {
    const matrix = new Matrix4();
    matrix.fromArray(elements);
    return matrix;
};

const decomposePivotMatrix = (toothData) => {
    toothData.pivot.matrix.decompose(
        toothData.pivot.position,
        toothData.pivot.quaternion,
        toothData.pivot.scale
    );
};

const dispatchVersionUpdate = (id, applyOnComparative) => {
    const selectedVersionNumber = parseInt(id, 10);
    if (applyOnComparative) {
        dispatchComparativeVersionUpdate(selectedVersionNumber);
    } else {
        dispatchMainVersionUpdate(selectedVersionNumber);
    }
};

const dispatchComparativeVersionUpdate = (versionNumber) => {
    Store.dispatch({ type: SET_SELECTED_COMPARATIVE_VERSION, payload: versionNumber });
    showComparativeElement(true);
};

const dispatchMainVersionUpdate = (versionNumber) => {
    Store.dispatch({ type: SET_SELECTED_VERSION, payload: versionNumber });
    Store.dispatch(setKeyframeToEnd(getTimes()));
};
